/**
 * OrgChartTree — live SVG visualization of the church leadership hierarchy.
 *
 * Reads the pre-computed `tree` payload from `GET /admin/leadership/org-chart`
 * (7-level → category groups → position nodes). Each node card shows:
 *   • Position name + acting/vacant badge
 *   • Every current holder's name + unit chip
 *
 * Purely CSS/flex — no external tree library — so it stays snappy and
 * matches the existing slate/amber palette.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Users, UserPlus, ChevronDown } from 'lucide-react';

const LEVEL_LABELS = {
  global_hq: 'Global HQ',
  province: 'Province',
  region: 'Region',
  district: 'District',
  campus: 'Campus',
  assembly: 'Branch / Assembly',
  department: 'Department',
  home_group: 'Home Group',
};

const CATEGORY_TONE = {
  executive: 'border-amber-500/40 bg-amber-500/5',
  episcopal: 'border-violet-500/40 bg-violet-500/5',
  bible_school: 'border-sky-500/40 bg-sky-500/5',
  ministry_directors: 'border-emerald-500/40 bg-emerald-500/5',
  ministry: 'border-emerald-500/30 bg-emerald-500/5',
  leadership: 'border-indigo-500/40 bg-indigo-500/5',
  admin: 'border-slate-500/40 bg-slate-500/5',
  finance: 'border-rose-500/40 bg-rose-500/5',
  custom: 'border-slate-600 bg-slate-800/40',
  other: 'border-slate-700 bg-slate-800/30',
};

const CATEGORY_LABEL = {
  executive: 'Executive Leadership',
  episcopal: 'Episcopal Leadership',
  bible_school: 'Bible School Leadership',
  ministry_directors: 'Ministry Directors',
  ministry: 'Ministry Leaders',
  leadership: 'Leadership',
  admin: 'Administration',
  finance: 'Finance',
  custom: 'Custom',
  other: 'Other',
};

function PositionCard({ node, onSelect }) {
  const tone = CATEGORY_TONE[node.category] || CATEGORY_TONE.other;
  const holders = node.holders || [];
  const isVacant = node.is_vacant;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(node)}
      data-testid={`orgchart-node-${node.id}`}
      className={`group w-full min-w-0 sm:min-w-[180px] max-w-[240px] rounded-lg border p-2 text-left transition-all hover:scale-[1.02] hover:shadow-lg ${tone}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="text-xs font-semibold text-slate-100 leading-tight">
          {node.name}
        </div>
        {node.multiple_holders_allowed && (
          <Badge variant="outline" className="border-slate-600 text-[9px] text-slate-400 px-1 py-0">
            multi
          </Badge>
        )}
      </div>

      {isVacant ? (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-rose-300/80">
          <AlertCircle className="h-3 w-3" />
          Vacant
        </div>
      ) : (
        <div className="mt-1.5 space-y-0.5">
          {holders.slice(0, 3).map((h) => (
            <div key={h.id} className="flex items-center gap-1 text-[10px]">
              <span className="text-slate-100 truncate">{h.person_name || '—'}</span>
              {h.is_acting && (
                <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[8px] px-1 py-0">
                  acting
                </Badge>
              )}
              {h.unit_name && (
                <span className="text-slate-500 truncate">· {h.unit_name}</span>
              )}
            </div>
          ))}
          {holders.length > 3 && (
            <div className="text-[9px] text-slate-500">+{holders.length - 3} more</div>
          )}
        </div>
      )}
    </button>
  );
}

function CategoryBlock({ category, nodes, onSelectNode }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-2">
      <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">
        {CATEGORY_LABEL[category] || category}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {nodes.map((n) => (
          <PositionCard key={n.id} node={n} onSelect={onSelectNode} />
        ))}
      </div>
    </div>
  );
}

function LevelRow({ level, label, categories, positionsCount, onSelectNode }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <div className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-200 whitespace-nowrap">
          {label}
        </div>
        <div className="h-px flex-1 bg-gradient-to-r from-amber-500/40 to-transparent" />
        <span className="text-[10px] text-slate-500">
          {positionsCount} position{positionsCount === 1 ? '' : 's'}
        </span>
      </div>
      <div className="mt-2 space-y-2 pl-2 sm:pl-6">
        {categories.map((c) => (
          <CategoryBlock
            key={c.category}
            category={c.category}
            nodes={c.nodes}
            onSelectNode={onSelectNode}
          />
        ))}
        {categories.length === 0 && (
          <div className="rounded border border-dashed border-slate-800 p-3 text-center text-[10px] text-slate-500">
            No positions defined for this level.
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrgChartTree({ tree, onSelectNode }) {
  if (!tree || tree.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 p-6 text-center text-xs text-slate-500">
        Loading org chart…
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="orgchart-tree">
      {tree.map((row, idx) => (
        <React.Fragment key={row.level}>
          <LevelRow
            level={row.level}
            label={LEVEL_LABELS[row.level] || row.label}
            categories={row.categories || []}
            positionsCount={row.positions_count || 0}
            onSelectNode={onSelectNode}
          />
          {idx < tree.length - 1 && (
            <div className="flex justify-center py-0.5" aria-hidden="true">
              <ChevronDown className="h-3 w-3 text-slate-700" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export { PositionCard };
